/**
 * 发送验证码的自定义 Hook
 * 
 * 这是一个简易的发送验证码 Hook，只接收两个参数：
 * - account: 目标账户（邮箱或手机号）
 * - scene: 发送类型（场景）
 * 
 * 提供loading状态管理和错误处理
 */

import { useState, useCallback } from 'react'
import { APIWeb } from '@/api'
import { Toast } from 'react-vant'
import { useTranslation } from 'react-i18next'

/**
 * 发送验证码的场景类型
 */
export type SendCodeScene = 'register' | 'password_reset' | 'cvv_view' | 'change_account'

/**
 * useSendCode Hook 的返回值类型
 */
export interface UseSendCodeReturn {
    /** 是否正在发送验证码 */
    sendCodeLoading: boolean
    /** 发送验证码的方法 */
    sendCode: (params: { account: string, scene: SendCodeScene, callBack?: () => void }) => Promise<SendCodeResponse | null>
}
export const useSendCode = (): UseSendCodeReturn => {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)

    /**
     * 发送验证码
     * @param account 目标账户（邮箱或手机号）
     * @param scene 发送类型（场景）
     * @returns Promise<SendCodeResponse | null> 发送结果，失败时返回null
     */
    const sendCode = useCallback(async (params: { account: string, scene: SendCodeScene, callBack?: () => void }): Promise<SendCodeResponse | null> => {
        setLoading(true)
        try {
            const result = await APIWeb.sendCode({
                account: params.account,
                scene: params.scene
            })
            Toast.success({
                message: t('验证码已发送'),
                onClose: params.callBack || (() => { })
            })
            return result
        } catch (error) {
            console.error('发送验证码失败:', error)
            return null
        } finally {
            setLoading(false)
        }
    }, [loading, t])

    /**
     * 重置loading状态
     */

    return {
        sendCodeLoading: loading,
        sendCode
    }
}

export default useSendCode